import { useResolveFlagMutation } from "./store/hooks";
import { IconButton, ListItemText, Skeleton, SvgIcon } from "@mui/material";
import { processDisplay, processPicture } from "../student-selection/videoplayer/Timestamps/TimestampLinks";
import { FlagLevel, ProctoringEvent, ProctoringEventInfo } from "./store/slices/proctoringSession";
import i18n from "../../../i18n";
import CompositeTable, {
	CompositeTableBodyRow,
	CompositeTableCell,
	CompositeTableContainer,
	CompositeTableHeaderRow,
	CompositeTableNoResults,
} from "../../../components/composite-table";
import styles from "./ProctoringEventsTable.module.css";
import MoreVert from "../../../images/portal/more-vert.svg?react";
import { useContext, useRef, useState } from "react";
import { CoreMenu, CoreMenuItem } from "@verity/components/core";
import convertEventFormat from "./convertEventFormat";
import { ProctoringResultsContext } from "./context";
import ResolveModal from "../ResolveModal";

const FLAG_LEVEL_TO_SEVERITY_NAME = [undefined, "minor", "major", "severe"] as const;

function FlagLevelBadge({ flagLevel }: { flagLevel: FlagLevel }) {
	const severityName = FLAG_LEVEL_TO_SEVERITY_NAME[flagLevel] ?? "none";
	return (
		<div className={`${styles.flagLevelBadge} ${styles[severityName]}`}>
			{i18n.t(`result.components.resultsPage.proctoringEventsTableTSX.${severityName}`)}
		</div>
	);
}

export function formatEventTimestamp(time: number, totalDuration = 0) {
	const durationMinutes = totalDuration / 1000 / 60;
	const seconds = Math.round(time / 1000);
	const secondsRemainder = seconds % 60;
	const minutes = Math.round((seconds - secondsRemainder) / 60);
	const minutesRemainder = minutes % 60;

	const secondsText = String(secondsRemainder).padStart(2, "0");
	if (durationMinutes < 10) {
		return `${minutesRemainder}:${secondsText}`;
	}
	const minutesText = `${String(minutesRemainder).padStart(2, "0")}:${secondsText}`;
	if (durationMinutes < 60) {
		return minutesText;
	}

	const durationHours = Math.round(durationMinutes / 60);
	const totalHours = Math.round((minutes - minutesRemainder) / 60);
	const hours = totalHours % 60;
	return `${String(hours).padStart(String(durationHours).length, "0")}:${minutesText}`;
}

function EventMenu({ event }: { event: ProctoringEvent }) {
	const menuButtonRef = useRef<HTMLButtonElement>(null);
	const [menuShowing, setMenuShowing] = useState(false);

	const [resolveModalShowing, setResolveModalShowing] = useState(false);
	const [resolveFlag] = useResolveFlagMutation();

	const closeMenu = () => setMenuShowing(false);

	return (
		<>
			<IconButton ref={menuButtonRef} onClick={() => setMenuShowing(true)}>
				<SvgIcon>
					<MoreVert />
				</SvgIcon>
			</IconButton>
			{menuShowing && (
				<CoreMenu open onClose={closeMenu} anchorEl={menuButtonRef.current}>
					<CoreMenuItem
						onClick={async () => {
							setResolveModalShowing(true);
							closeMenu();
						}}
					>
						<ListItemText>{i18n.t("result.components.resultsPage.proctoringEventsTableTSX.resolve")}</ListItemText>
					</CoreMenuItem>
				</CoreMenu>
			)}
			{resolveModalShowing && (
				<ResolveModal
					onClose={() => setResolveModalShowing(false)}
					comment={event.anomaly?.comment}
					onConfirm={async (comment) => {
						await resolveFlag({ eventId: event.id, comment });
					}}
				/>
			)}
		</>
	);
}

function EventRow({ event, totalDuration }: { event: ProctoringEvent; totalDuration: number }) {
	const { time, anomaly } = event;
	const convertedEvent = convertEventFormat(event);
	const eventIcon = processPicture(convertedEvent);

	return (
		<CompositeTableBodyRow>
			<CompositeTableCell>
				{eventIcon
					? eventIcon({
							style: {
								width: "24px",
								height: "24px",
							},
						})
					: null}
			</CompositeTableCell>
			<CompositeTableCell className={styles.flagDescription}>
				{processDisplay(convertedEvent, false, true)}
			</CompositeTableCell>
			<CompositeTableCell>
				<FlagLevelBadge flagLevel={anomaly?.flagLevel ?? 0} />
			</CompositeTableCell>
			<CompositeTableCell>{formatEventTimestamp(time, totalDuration)}</CompositeTableCell>
			<CompositeTableCell>
				<EventMenu event={event} />
			</CompositeTableCell>
		</CompositeTableBodyRow>
	);
}

export function ProctoringEventsTable({
	proctoringEventInfo,
	isLoading,
	isError,
	className,
}: {
	proctoringEventInfo?: ProctoringEventInfo;
	isLoading: boolean;
	isError: boolean;
	className?: string;
}) {
	const { config } = useContext(ProctoringResultsContext);
	const proctoringEvents = proctoringEventInfo?.events;
	const isLoaded = !isLoading && !isError;

	if (isLoading) {
		return <Skeleton variant="rectangular" height="100%" sx={{ flex: 1 }} />;
	}
	const hasEvents = isLoaded && proctoringEvents && proctoringEvents.length > 0;

	const currTimestamp = Date.now();
	const totalDuration = proctoringEventInfo
		? (proctoringEventInfo?.endProctoringTime ?? currTimestamp) - (proctoringEventInfo.startProctoringTime ?? 0)
		: 0;
	const startTime = proctoringEventInfo?.startQuizTime ?? proctoringEventInfo?.startRecordingTime;
	const timeOffset = startTime ? startTime - (proctoringEventInfo?.startProctoringTime ?? 0) : 0;

	return (
		<>
			<CompositeTableContainer className={className}>
				<CompositeTable
					className={styles.table}
					stickyHeader
					head={
						<CompositeTableHeaderRow
							columns={[
								{
									key: "type",
									content: i18n.t("result.components.resultsPage.proctoringEventsTableTSX.type"),
									width: 60,
								},
								{
									key: "description",
									content: i18n.t("result.components.resultsPage.proctoringEventsTableTSX.flagDescription"),
								},
								{
									key: "flagLevel",
									content: i18n.t("result.components.resultsPage.proctoringEventsTableTSX.flagLevel"),
									width: 120,
								},
								{
									key: "time",
									content: i18n.t("result.components.resultsPage.proctoringEventsTableTSX.time"),
									width: 70,
								},
								{
									key: "actions",
									content: i18n.t("result.components.resultsPage.proctoringEventsTableTSX.actions"),
									width: 120,
								},
							]}
						/>
					}
				>
					{hasEvents &&
						proctoringEvents.map((event) => (
							<EventRow
								key={event.id}
								event={{ ...event, time: event.time - timeOffset }}
								totalDuration={totalDuration}
							/>
						))}
				</CompositeTable>
				{isError && (
					<CompositeTableNoResults>
						{i18n.t("result.components.resultsPage.proctoringEventsTableTSX.errorLoading")}
					</CompositeTableNoResults>
				)}
				{isLoaded && proctoringEvents && proctoringEvents.length === 0 && (
					<CompositeTableNoResults>
						{config.showFlaggedOnly
							? i18n.t("result.components.resultsPage.proctoringEventsTableTSX.noFlags")
							: i18n.t("result.components.resultsPage.proctoringEventsTableTSX.noEvents")}
					</CompositeTableNoResults>
				)}
			</CompositeTableContainer>
		</>
	);
}
